import '.a{display:flex;}.ssr-plugin'
import '.ax{justify-content:center;}.ssr-plugin'
import '.t{align-items:center;}.ssr-plugin'
import '.n{width:100%;}.ssr-plugin'
import '.ar{height:100%;}.ssr-plugin'
import '.hj{transition:background .07s ease, opacity .07s ease;}.ssr-plugin'
import '.ab{position:relative;}.ssr-plugin'
import '.b{flex-direction:column;}.ssr-plugin'
import '.hk{max-height:85vh;}.ssr-plugin'
import '.ak{border-radius:1.25rem;}.ssr-plugin'
import '.b5{overflow:hidden;}.ssr-plugin'
import '.hl{box-shadow:0 24px 60px rgba(20,21,43,0.25);}.ssr-plugin'
import '.bx{position:absolute;}.ssr-plugin'
import '.hm{top:1rem;}.ssr-plugin'
import '.fo{right:1rem;}.ssr-plugin'
import '.c9{z-index:2;}.ssr-plugin'
import '.e6{gap:0.25rem;}.ssr-plugin'
import '.au{aspect-ratio:1/1;}.ssr-plugin'
import '.f0{height:1.5rem;}.ssr-plugin'
import '.ez{width:1.5rem;}.ssr-plugin'
import '.hn{border-radius:0.375rem;}.ssr-plugin'
import '.co{font-size:1.125rem;}.ssr-plugin'
import '.c1{border:none;}.ssr-plugin'
import '.ho{background:rgba(20,21,43,0.06);}.ssr-plugin'
import '.a1{cursor:pointer;}.ssr-plugin'
import '.a5{color:var(--brand-secondary);}.ssr-plugin'
import '.aq{flex-shrink:0;}.ssr-plugin'
import '.br{overflow-y:auto;}.ssr-plugin'
import '.at{flex:1;}.ssr-plugin'
import '.dk{min-height:0px;}.ssr-plugin'
import '.bu{position:sticky;}.ssr-plugin'
import '.bf{top:0px;}.ssr-plugin'
import '.e1{z-index:1;}.ssr-plugin'
import '.hp{padding:1rem 3.5rem 1rem 1.5rem;}.ssr-plugin'
import '.c4{text-decoration:none;}.ssr-plugin'
import '.hq{height:1px;}.ssr-plugin'
import '.hr{background-color:var(--Shades-Blue-100, #DBDCE7);}.ssr-plugin'
import '.dn{padding:1rem 1.5rem;}.ssr-plugin'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuthState, useClient, useQuery } from '@based/sdk/react'
import { styled } from 'inlines'
import { faEllipsis, faShare, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Avatar } from '@app/components/core/avatar/Avatar'
import { IconButton } from '@app/components/core/button/IconButton'
import { Button } from '@app/components/core/button/Button'
import {
  DropdownMenu,
  type DropdownMenuItem,
} from '@app/components/core/dropdown/DropdownMenu'
import { Text } from '@app/components/core/typography/Text'
import { TextLink } from '@app/components/core/link/TextLink'
import { ModalWrapper } from '@app/components/blocks/modal/ModalWrapper'
import { SocialShareModal } from '@app/components/blocks/modal/SocialShareModal'
import { PostMediaGallery } from '@app/components/blocks/mediaGallery/PostMediaGallery'
import { FileDisplay } from '@app/components/core/fileDisplay/FileDisplay'
import { Tag } from '@app/components/core/tag/Tag'
import { Comment } from '@app/components/blocks/thread/blocks/Comment'
import { PostActivityRow } from '@app/components/blocks/thread/blocks/PostActivityRow'
import { ReplyTextBox } from '@app/components/blocks/thread/blocks/ReplyTextBox'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { PostMediaItem, PostModel } from '@app/types/Thread'
import { fullName } from '@app/util/helpers'
import useDateFormat from '@app/util/useDateFormat'
import useMediaQuery from '@app/util/useMediaQuery'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { useTranslations } from '@i18n/useLanguage'
import { pages } from '@app/util/links'
import { ViewPostModel } from 'functions/app/thread/post/view'
import {
  getPostedInLabel,
  getPostedInLinks,
  getPostShareTitle,
  getPostShareUrl,
} from '@app/pages/discussions/discussionsHelpers'
import {
  DiscussionCardCategory,
} from './DiscussionCard'
import { Title } from '@app/components/core/typography/Title'

const EASE_BOUNCE = 'cubic-bezier(.34,1.56,.64,1)'
const MODAL_EXIT_DURATION_MS = 350

type PostDetailModalProps = {
  postId: number | null
  threadId: number | null
  authorName: string
  postedIn?: string
  date: string
  title?: string
  updatedAt?: string
  categories?: DiscussionCardCategory[]
  onClose: () => void
  onEdit?: () => void
  onDelete?: () => void
}

const getCardBackgroundColour = (
  categories?: DiscussionCardCategory[],
): string => {
  if (categories?.length === 1) {
    return categories[0].backgroundColour
  }
  return '#FFFFFF'
}

type LikeToggleNode = {
  id: number
  viewers?: { id: number; $reaction?: string }[]
  replies?: LikeToggleNode[]
}

// Optimistically toggles the current user's like on the matching post anywhere
// in the tree (root post or any nested reply), preserving all other fields.
const toggleLike = <T extends LikeToggleNode>(
  node: T,
  postId: number,
  userId: number,
): T => {
  let next: T = node

  if (node.id === postId) {
    const viewers = node.viewers ?? []
    const alreadyLiked = viewers.some(
      (viewer) => viewer.id === userId && viewer.$reaction === 'like',
    )
    next = {
      ...node,
      viewers: alreadyLiked
        ? viewers.filter(
            (viewer) =>
              !(viewer.id === userId && viewer.$reaction === 'like'),
          )
        : [...viewers, { id: userId, $reaction: 'like' }],
    }
  }

  if (next.replies && next.replies.length > 0) {
    next = {
      ...next,
      replies: next.replies.map((reply) => toggleLike(reply, postId, userId)),
    }
  }

  return next
}

const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

const mapPost = (post: ViewPostModel): PostModel => {
  return {
    id: post.id,
    authorId: post.author?.id,
    authorName: post.author
      ? fullName({
          firstName: post.author.firstName,
          lastName: post.author.lastName,
          middlePart: post.author.middlePart,
        })
      : '',
    authorAvatar: post.author?.avatar?.src,
    authorHandle: post.author?.handle,
    creationDate: new Date(post.createdAt),
    content: post.content,
    media: (post.media ?? [])
      .filter((item) => item.status === 'ready' || item.status === '__local__')
      .map((item) => ({
        src: item.src,
        mimeType: item.mimeType,
        name: item.name,
        uploading: item.status === '__local__',
      })),
    categories: (post.categories ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      primaryColour: c.primaryColour,
      backgroundColour: c.backgroundColour,
      borderColour: c.borderColour,
    })),
    reactionUserIds: (post.viewers ?? [])
      .filter((viewer) => viewer.$reaction === 'like')
      .map((viewer) => viewer.id.toString()),
    comments: (post.replies ?? []).map((r) =>
      mapPost(r as unknown as ViewPostModel),
    ),
    hasMoreComments: false,
  }
}

export const PostDetailModal = ({
  postId,
  threadId,
  authorName,
  postedIn,
  date,
  title,
  updatedAt,
  categories,
  onClose,
  onEdit,
  onDelete,
}: PostDetailModalProps) => {
  const isMobile = useMediaQuery()
  const dateFormat = useDateFormat(true)
  const t = useTranslations().app.discussions
  const tCommon = useTranslations().app.common
  const client = useClient()
  const authState = useAuthState()
  // Fetch the logged-in user's display info so optimistic replies can show
  // the correct author name and avatar immediately after submission.
  const { data: rawProfileData } = useQuery(
    'view-profile',
    authState.userId ? { id: Number(authState.userId) } : null,
  )
  const currentUserProfile = rawProfileData as {
    firstName?: string
    lastName?: string
    middlePart?: string
    avatar?: { src?: string }
  } | null
  const { withAuthCheck } = useAuthModal()
  const postReplyRef = useRef<HTMLInputElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isClosingRef = useRef(false)
  const [isVisible, setIsVisible] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [post, setPost] = useState<ViewPostModel | null>(null)
  const [loading, setLoading] = useState(true)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  // Keyed by real post ID — persists data: URL previews across subscription updates
  // until the server confirms the file is ready (or the modal closes).
  const [pendingMedia, setPendingMedia] = useState<Map<number, PostMediaItem[]>>(
    () => new Map(),
  )

  useEffect(() => {
    if (!postId) {
      setPost(null)
      setLoading(false)
      return
    }
    setLoading(true)
    const close = client
      .query('view-post', { id: postId })
      .subscribe((result) => {
        setPost(result as ViewPostModel)
        setLoading(false)
      })
    return () => {
      close()
    }
  }, [client, postId])

  // Prefer thread.id from the fetched post so the modal is self-contained.
  // Fall back to the prop (passed from the discussions list) for when data is loading.
  const effectiveThreadId = post?.threads?.[0]?.id ?? threadId

  const postModel = useMemo(() => {
    if (!post) {
      return null
    }
    const mapped = mapPost(post)
    if (pendingMedia.size === 0) return mapped
    // Merge pending data: URL previews into each comment / sub-comment so they
    // remain visible while the real upload is in flight.
    const augment = (comment: PostModel): PostModel => {
      const pending = pendingMedia.get(comment.id)
      const withPending: PostModel = pending
        ? { ...comment, media: [...comment.media, ...pending] }
        : comment
      if (withPending.comments.length === 0) return withPending
      return { ...withPending, comments: withPending.comments.map(augment) }
    }
    return { ...mapped, comments: mapped.comments.map(augment) }
  }, [post, pendingMedia])

  const displayCategories = post?.categories ?? categories
  const backgroundColour = getCardBackgroundColour(displayCategories)
  const displayAuthorName = postModel?.authorName || authorName
  const displayDate = postModel
    ? dateFormat(postModel.creationDate)
    : date
  const displayUpdatedAt =
    post?.updatedAt && post.updatedAt > post.createdAt
      ? dateFormat(new Date(post.updatedAt))
      : updatedAt
  const displayTitle = post?.title ?? title
  const displayContent = postModel?.content ?? ''
  const shareUrl = postId ? getPostShareUrl(postId) : undefined
  const shareTitle = getPostShareTitle(displayTitle, displayContent)
  const allMedia = postModel?.media ?? []
  const displayMedia = allMedia
    .filter((item) => item.mimeType !== 'application/pdf')
    .map((item) => item.src)
  const displayDocuments = allMedia.filter(
    (item) => item.mimeType === 'application/pdf',
  )
  const postedInLabel = post?.threads
    ? getPostedInLabel(post.threads)
    : postedIn
  const postedInPrefix = t.postedIn.replace('{0}', '').trimEnd()
  const postedInLinks = post?.threads ? getPostedInLinks(post.threads) : []
  const authorProfileUrl = post?.author?.handle
    ? pages.profile(post.author.handle)
    : undefined

  const linkTextStyle = {
    color: 'var(--brand-secondary)',
    lineHeight: '1.4',
  } as const

  const menuItems: DropdownMenuItem[] = []
  if (onEdit) {
    menuItems.push({
      id: 'edit',
      label: t.menuEdit,
      linkProps: { onClick: onEdit, textStyle: { textWrap: 'nowrap' } },
    })
  }
  if (onDelete) {
    menuItems.push({
      id: 'delete',
      label: t.menuDelete,
      linkProps: { onClick: onDelete, textStyle: { textWrap: 'nowrap' } },
    })
  }

  useEffect(() => {
    setMounted(true)
  }, [])

  // Trigger the enter animation whenever a post opens.
  // Reset to closed first so switching between posts re-plays the animation.
  useEffect(() => {
    if (!postId) {
      return
    }
    isClosingRef.current = false
    setIsVisible(false)
    const raf = requestAnimationFrame(() => setIsVisible(true))
    return () => cancelAnimationFrame(raf)
  }, [postId])

  const handleClose = useCallback(() => {
    if (isClosingRef.current) return
    isClosingRef.current = true
    setIsVisible(false)
    setTimeout(() => {
      isClosingRef.current = false
      onClose()
    }, MODAL_EXIT_DURATION_MS)
  }, [onClose])

  // Clear pending media entries once the subscription delivers a confirmed
  // `status: 'ready'` item for that reply, so the server's real URL takes over.
  useEffect(() => {
    if (!post || pendingMedia.size === 0) return
    const toRemove: number[] = []
    type ReplyLike = { id: number; media?: Array<{ status: string }>; replies?: ReplyLike[] }
    const checkReplies = (replies: ReplyLike[]) => {
      for (const reply of replies) {
        if (pendingMedia.has(reply.id) && (reply.media ?? []).some((m) => m.status === 'ready')) {
          toRemove.push(reply.id)
        }
        if (reply.replies) {
          checkReplies(reply.replies)
        }
      }
    }
    checkReplies(post.replies ?? [])
    if (toRemove.length > 0) {
      setPendingMedia((prev) => {
        const next = new Map(prev)
        toRemove.forEach((id) => next.delete(id))
        return next
      })
    }
  }, [post, pendingMedia])

  const hasReaction = !!(
    mounted &&
    authState.userId &&
    postModel?.reactionUserIds.includes(authState.userId.toString())
  )

  const replyCount = useMemo(() => {
    if (!postModel) {
      return 0
    }
    return postModel.comments.reduce(
      (total, comment) => total + 1 + comment.comments.length,
      0,
    )
  }, [postModel])

  const react = useCallback(
    (reactPostId: number) => {
      void withAuthCheck(async () => {
        const userId = authState.userId ? Number(authState.userId) : null
        if (userId) {
          // Optimistically toggle so the heart and count update instantly.
          setPost((prev) =>
            prev ? toggleLike(prev, reactPostId, userId) : prev,
          )
        }
        await client.call('react-post', { postId: reactPostId })
      })
    },
    [client, withAuthCheck, authState.userId],
  )

  const reply = useCallback(
    async (parentPostId: number, text: string, files: File[] = []): Promise<boolean> => {
      if (!text.trim()) {
        return false
      }

      // Convert image files to data: URLs (CSP-compliant, no blob: URLs needed).
      const media = await Promise.all(
        files.map(async (file) => ({
          src: file.type.startsWith('image/') ? await fileToDataUrl(file) : '',
          status: '__local__' as const,
          mimeType: file.type,
          name: file.name,
        })),
      )

      // Optimistically append the reply to local state so the user sees it
      // immediately without waiting for a server round-trip.
      const now = new Date().toISOString()
      const optimisticReply = {
        id: -Date.now(),
        content: text,
        createdAt: now,
        updatedAt: now,
        author: authState.userId
          ? {
              id: Number(authState.userId),
              firstName: currentUserProfile?.firstName ?? '',
              lastName: currentUserProfile?.lastName ?? '',
              middlePart: currentUserProfile?.middlePart ?? '',
              avatar: { src: currentUserProfile?.avatar?.src },
              handle: undefined as string | undefined,
            }
          : undefined,
        replies: [] as never[],
        viewers: [] as { id: number; $reaction?: string }[],
        media,
      }

      setPost((prev) => {
        if (!prev) return prev
        if (parentPostId === prev.id) {
          // Direct reply to the root post — append to its replies.
          return { ...prev, replies: [...(prev.replies ?? []), optimisticReply as any] }
        }
        // Reply to a comment — find the matching comment and append there.
        return {
          ...prev,
          replies: (prev.replies ?? []).map((r) =>
            r.id === parentPostId
              ? { ...r, replies: [...(r.replies ?? []), optimisticReply as any] }
              : r,
          ),
        }
      })

      // Scroll to the bottom of the modal after React paints the new comment.
      requestAnimationFrame(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight
        }
      })

      // Persist to the backend without blocking the UI.
      // threadId is optional – a reply linked via parentPostId is sufficient.
      void withAuthCheck(async () => {
        const result = await client.call('add-post', {
          ...(effectiveThreadId ? { threadId: effectiveThreadId } : {}),
          parentPostId,
          content: text.trim(),
        })
        if (result && files.length > 0) {
          const newPostId = Number(result)
          // Store the data: URL previews so they survive subscription replacements.
          if (media.length > 0) {
            setPendingMedia((prev) => {
              const next = new Map(prev)
              next.set(
                newPostId,
                media.map((m) => ({ src: m.src, mimeType: m.mimeType, name: m.name, uploading: true })),
              )
              return next
            })
          }
          // Upload all files, then remove the spinner once done regardless of
          // whether the subscription re-fires (deployed servers may not emit
          // media-status changes in real time).
          await Promise.allSettled(
            files.map((file) =>
              client.stream('upload-file', {
                mimeType: file.type,
                contents: file,
                size: file.size,
                fileName: file.name,
                payload: { postMedia: { id: newPostId } },
              }),
            ),
          )
          setPendingMedia((prev) => {
            const entry = prev.get(newPostId)
            if (!entry) return prev
            const next = new Map(prev)
            next.set(newPostId, entry.map((m) => ({ ...m, uploading: false })))
            return next
          })
        }
      })

      return true
    },
    [client, effectiveThreadId, withAuthCheck, authState.userId, currentUserProfile, setPendingMedia],
  )

  if (!postId) {
    return null
  }

  return (
    <ModalWrapper style={{ backgroundColor: 'transparent' }}>
      <div className="a ax t n ar hj"
        onClick={handleClose}
        style={{
          background: isVisible ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0)',
          opacity: isVisible ? 1 : 0,
          pointerEvents: isVisible ? 'auto' : 'none',}}
      >
        <div className="ab a b hk ak b5 hl"
          onClick={(event) => event.stopPropagation()}
          style={{
            width: isMobile ? '90vw' : 'min(600px, 90vw)',
            backgroundColor: backgroundColour,
            transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(28px) scale(0.97)',
            opacity: isVisible ? 1 : 0,
            transition: `transform .38s ${EASE_BOUNCE}, opacity .28s ease`,
          }}
        >
        <div className="bx hm fo c9 a t e6"
        >
          {menuItems.length > 0 && (
            <styled.div onClick={(event) => event.stopPropagation()}>
              <DropdownMenu items={menuItems} alignment="end">
                <IconButton
                  variant="transparent"
                  size="small"
                  icon={faEllipsis}
                  onClick={() => {}}
                  style={{ background: 'transparent', border: 'none' }}
                />
              </DropdownMenu>
            </styled.div>
          )}
          <styled.button className="a ax t au f0 ez hn co c1 ho a1 a5 aq"
            onClick={handleClose}
            style={{
              transition: `background .15s ease, transform .15s ${EASE_BOUNCE}`,
              '&:hover': {
                background: 'rgba(20,21,43,0.12)',
                transform: 'scale(1.08)',
              },
            }}
          >
            <FontAwesomeIcon icon={faXmark} />
          </styled.button>
        </div>

        <div className="br at dk"
          ref={scrollContainerRef}
          style={{
            backgroundColor: backgroundColour,
          }}
        >
          <div className="bu bf e1 hp"
            style={{
              backgroundColor: backgroundColour,
              boxShadow: `0 1px 0 0 ${backgroundColour}`,
            }}
          >
            <Row gap="0.75rem" style={{ alignItems: 'flex-start' }}>
              <Avatar
                size="small"
                image={postModel?.authorAvatar}
                labelSource={displayAuthorName}
              />
              <Column gap="0.25rem" style={{ flex: 1, minWidth: 0 }}>
                <Row gap="0.25rem" style={{ flexWrap: 'wrap', alignItems: 'baseline' }}>
                  {authorProfileUrl ? (
                    <TextLink
                      to={authorProfileUrl}
                      target="_blank"
                      isBold={true}
                      textStyle={linkTextStyle}
                      containerStyle={{ textDecoration: 'none', display: 'inline' }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {displayAuthorName}
                    </TextLink>
                  ) : (
                    <Text.Medium
                      isBold={true}
                      style={{
                        color: 'var(--brand-secondary)',
                        lineHeight: '1.4',
                      }}
                    >
                      {displayAuthorName}
                    </Text.Medium>
                  )}
                  {postedInLabel && (
                    <Text.Medium
                      style={{
                        color: 'var(--brand-secondary)',
                        lineHeight: '1.4',
                        overflowWrap: 'anywhere',
                        paddingRight: menuItems.length > 0 ? '2.5rem' : undefined,
                      }}
                    >
                    {`${postedInPrefix} `}
                    {postedInLinks.length > 0 ? (
                      postedInLinks.map((entity, index) => (
                        <Fragment key={`${entity.label}-${index}`}>
                          {index > 0 && ', '}
                          {entity.link ? (
                            <TextLink
                              to={entity.link}
                              target="_blank"
                              isBold={true}
                              textStyle={linkTextStyle}
                              containerStyle={{
                                textDecoration: 'none',
                                display: 'inline',
                              }}
                              onClick={(event) => event.stopPropagation()}
                            >
                              {entity.label}
                            </TextLink>
                          ) : (
                            <Text.Medium
                              isBold={true}
                              style={{
                                color: 'var(--brand-secondary)',
                                lineHeight: '1.4',
                                display: 'inline',
                              }}
                            >
                              {entity.label}
                            </Text.Medium>
                          )}
                        </Fragment>
                      ))
                    ) : (
                      <Text.Medium
                        isBold={true}
                        style={{
                          color: 'var(--brand-secondary)',
                          lineHeight: '1.4',
                        }}
                      >
                        {postedInLabel}
                      </Text.Medium>
                    )}
                  </Text.Medium>
                  )}
                </Row>
                <Text.Small style={{ color: 'var(--gray-300)', lineHeight: '1.4' }}>
                  {displayDate}
                  {displayUpdatedAt ? ` | ${t.editedLabel} ${displayUpdatedAt}` : ''}
                </Text.Small>
              </Column>
            </Row>
          </div>

          <Column
            gap="1rem"
            alignItems="stretch"
            style={{ padding: '0 1.5rem 1rem 1.5rem' }}
          >
            <Column gap="0.5rem" alignItems="stretch">
              {displayTitle && (
                <Title.H5
                  style={{ color: 'var(--brand-secondary)', lineHeight: '1.4' }}
                >
                  {displayTitle}
                </Title.H5>
              )}

              {displayMedia.length > 0 && (
                <PostMediaGallery images={displayMedia} />
              )}

              {loading && !postModel ? (
                <Text.Medium style={{ color: 'var(--gray-300)' }}>
                  Loading...
                </Text.Medium>
              ) : (
                <Text.Medium
                  style={{
                    color: 'var(--brand-secondary)',
                    lineHeight: '1.75rem',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {displayContent}
                </Text.Medium>
              )}

              {displayDocuments.length > 0 && (
                <Column gap="0.5rem" alignItems="stretch">
                  {displayDocuments.map((doc) => (
                    <a className="c4"
                      key={doc.src}
                      href={doc.src}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <FileDisplay
                        type="document"
                        name={doc.name ?? doc.src.split('/').pop() ?? 'document.pdf'}
                        src={doc.src}
                      />
                    </a>
                  ))}
                </Column>
              )}

              {displayCategories && displayCategories.length > 0 && (
                <Row gap="0.5rem" style={{ flexWrap: 'wrap', marginTop: '1.5rem' }}>
                  {displayCategories.map((category) => (
                    <Tag
                      key={category.id}
                      label={category.name}
                      variant="accent"
                      size="small"
                      style={{
                        backgroundColor: category.primaryColour,
                        background: category.primaryColour,
                        border: `1px solid ${category.borderColour}`,
                      }}
                    />
                  ))}
                </Row>
              )}
            </Column>

            {postModel && (
              <Row
                style={{
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <PostActivityRow
                  version="post"
                  hasReaction={hasReaction}
                  reactions={postModel.reactionUserIds.length}
                  comments={replyCount}
                  onReactionClick={() => react(postModel.id)}
                  onReplyClick={() => postReplyRef.current?.focus()}
                />
                {shareUrl && (
                  <Button
                    variant="secondary"
                    size="small"
                    icon={faShare}
                    iconPosition="left"
                    label={tCommon.share}
                    onClick={() => setShareModalOpen(true)}
                    style={{ flexShrink: 0 }}
                  />
                )}
              </Row>
            )}

            <div className="hq hr n" />

            {postModel?.comments.map((comment, index) => (
              <Comment
                key={comment.id ?? index}
                post={comment}
                level="comment"
                onReact={react}
                onReply={reply}
              />
            ))}
          </Column>
        </div>

        <div className="aq dn"
          style={{
          backgroundColor: backgroundColour,
          }}
        >
          <ReplyTextBox
            ref={postReplyRef}
            onSumbit={async (text, files) => {
              if (!postId) {
                return false
              }
              return reply(postId, text, files)
            }}
          />
        </div>
      </div>
      </div>

      {shareUrl && (
        <div onClick={(e) => e.stopPropagation()}>
          <SocialShareModal
            isOpen={shareModalOpen}
            onClose={() => setShareModalOpen(false)}
            url={shareUrl}
            title={shareTitle}
          />
        </div>
      )}
    </ModalWrapper>
  )
}
